import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { BookOpen, Search, Plus, Trash2, FileText, Pencil, History } from "lucide-react";
import { fmtTimeAgo } from "@/lib/labels";

const KIND_LABEL = {
  sop: "SOP",
  support_policy: "Support policy",
  product_doc: "Product doc",
  escalation_playbook: "Escalation playbook",
  known_issue: "Known issue",
  account_commitment: "Account commitment",
  response_template: "Response template",
  other: "Other",
};

export default function Knowledge() {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ title: "", content: "", kind: "support_policy", source_url: "" });
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [msg, setMsg] = useState("");
  const [historyDocId, setHistoryDocId] = useState(null);
  const [revisions, setRevisions] = useState(null);

  const load = () => api.get("/knowledge").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 3500); };

  const closeForm = () => {
    setAdding(false);
    setEditingId(null);
    setDraft({ title: "", content: "", kind: "support_policy", source_url: "" });
  };

  const edit = async (id) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/knowledge/${id}`);
      setDraft({
        title: data.title,
        content: data.content || "",
        kind: data.kind,
        source_url: data.source_url || "",
      });
      setEditingId(id);
      setAdding(true);
    } catch (e) {
      flash(e?.response?.data?.message || "Could not load the document");
    } finally { setBusy(false); }
  };

  const toggleHistory = async (id) => {
    if (historyDocId === id) { setHistoryDocId(null); setRevisions(null); return; }
    setBusy(true);
    try {
      const { data } = await api.get(`/knowledge/${id}/revisions`);
      setRevisions(data.revisions || []);
      setHistoryDocId(id);
    } catch (e) {
      flash(e?.response?.data?.message || "Could not load history");
    } finally { setBusy(false); }
  };

  const viewRevision = async (id, revision) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/knowledge/${id}/revisions/${revision}`);
      setDraft({
        title: data.title,
        content: data.content || "",
        kind: data.kind,
        source_url: data.source_url || "",
      });
      setEditingId(id);
      setAdding(true);
      setHistoryDocId(null);
      setRevisions(null);
      flash(`Loaded revision ${revision} — press Save changes to restore it.`);
    } catch (e) {
      flash(e?.response?.data?.message || "Could not load that revision");
    } finally { setBusy(false); }
  };

  const search = async (e) => {
    e?.preventDefault();
    if (!query.trim()) { setResults(null); return; }
    setBusy(true);
    try {
      const { data } = await api.get(`/knowledge/search?q=${encodeURIComponent(query)}`);
      setResults(data.results);
    } catch (e) {
      flash(e?.response?.data?.message || "Search failed");
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!draft.title.trim() || !draft.content.trim()) return;
    setBusy(true);
    try {
      await api.post("/knowledge", { ...draft, source_url: draft.source_url.trim() || null });
      closeForm();
      await load();
      flash(editingId ? "Document updated and re-indexed." : "Document saved and indexed.");
    } catch (e) {
      flash(e?.response?.data?.message || "Could not save the document");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document? Its passages stop being retrievable.")) return;
    setBusy(true);
    try { await api.delete(`/knowledge/${id}`); await load(); flash("Deleted."); }
    catch (e) { flash(e?.response?.data?.message || "Delete failed"); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="p-8 text-[13px]" style={{ color: "var(--text-muted)" }}>Loading…</div>;

  return (
    <div className="px-4 py-5 md:px-8 md:py-6">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--text-muted)" }}>Knowledge Engine</div>
          <h1 className="font-display text-[26px]">Playbooks, policies and known issues</h1>
          <p className="text-[13px] mt-1" style={{ color: "var(--text-secondary)" }}>
            Retrieved into every AI assessment, and cited by passage — so a recommendation
            traces back to your own policy rather than to the model's judgement.
          </p>
        </div>
        {data.enabled && (
          <button className="btn" onClick={() => (adding ? closeForm() : setAdding(true))} data-testid="add-document">
            <Plus size={13} /> Add document
          </button>
        )}
      </div>

      {msg && <div className="badge b-muted mb-3" data-testid="knowledge-msg">{msg}</div>}

      {!data.enabled && (
        <div className="card p-5 mb-5" data-testid="knowledge-locked">
          <div className="text-[13px]">The knowledge layer is not included in this plan.</div>
          <div className="text-[12px] mt-1" style={{ color: "var(--text-muted)" }}>
            Cases still assess and score normally without it; they simply have no policy to cite.
          </div>
        </div>
      )}

      {adding && (
        <div className="card p-5 mb-5" data-testid="document-form">
          <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>
            {editingId ? "Editing document" : "New document"}
          </div>
          <input className="input mb-2" placeholder="Title, e.g. Refund and billing policy"
                 value={draft.title} disabled={!!editingId}
                 title={editingId ? "Title can't be changed here — delete and re-add to rename." : undefined}
                 onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          <select className="input mb-2" value={draft.kind}
                  onChange={(e) => setDraft({ ...draft, kind: e.target.value })}>
            {Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input className="input mb-2" placeholder="Source URL (optional)"
                 value={draft.source_url} onChange={(e) => setDraft({ ...draft, source_url: e.target.value })} />
          <textarea className="input font-mono text-[12px]" rows={10}
                    placeholder={"# Heading\n\nParagraphs are split into retrievable passages."}
                    value={draft.content}
                    onChange={(e) => setDraft({ ...draft, content: e.target.value })} />
          <div className="flex gap-2 mt-2">
            <button className="btn" onClick={save} disabled={busy} data-testid="save-document">
              {editingId ? "Save changes" : "Save & index"}
            </button>
            <button className="btn btn-ghost" onClick={closeForm}>Cancel</button>
          </div>
        </div>
      )}

      <form onSubmit={search} className="flex gap-2 mb-5">
        <input className="input" placeholder="Search the knowledge base…" value={query}
               onChange={(e) => setQuery(e.target.value)} data-testid="knowledge-search" />
        <button className="btn" type="submit" disabled={busy}><Search size={13} /> Search</button>
      </form>

      {results && (
        <div className="mb-6" data-testid="search-results">
          <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>
            {results.length} passage{results.length === 1 ? "" : "s"}
          </div>
          <div className="space-y-2">
            {results.map((r) => (
              <div key={r.chunk_id} className="card p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="badge b-muted">{KIND_LABEL[r.kind] || r.kind}</span>
                  <span className="text-[12.5px] font-medium">{r.document_title}</span>
                  {r.heading && <span className="text-[11.5px]" style={{ color: "var(--text-muted)" }}>› {r.heading}</span>}
                  <span className="ml-auto font-mono text-[10.5px]" style={{ color: "var(--text-muted)" }}>score {r.score}</span>
                </div>
                <div className="text-[12px] leading-snug" style={{ color: "var(--text-secondary)" }}>{r.content}</div>
                <div className="font-mono text-[10px] mt-1.5" style={{ color: "var(--text-muted)" }}>chunk {r.chunk_id}</div>
              </div>
            ))}
            {results.length === 0 && (
              <div className="text-[12.5px]" style={{ color: "var(--text-muted)" }}>
                Nothing matched. Search requires every word to appear; try fewer terms.
              </div>
            )}
          </div>
        </div>
      )}

      <div className="card overflow-x-auto" data-testid="knowledge-table">
        <table className="w-full min-w-[640px] text-[12.5px]">
          <thead>
            <tr className="text-left" style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Document</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Kind</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Passages</th>
              <th className="py-2 px-3 font-medium text-[11px] uppercase tracking-wider">Size</th>
              <th className="py-2 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.documents.length === 0 && (
              <tr><td colSpan={5} className="py-10 text-center" style={{ color: "var(--text-muted)" }}>
                <BookOpen size={18} className="inline mb-2" /><br />
                No documents yet.
              </td></tr>
            )}
            {data.documents.map((d) => (
              <React.Fragment key={d.id}>
                <tr style={{ borderBottom: "1px solid var(--border)" }} data-testid={`doc-${d.slug}`}>
                  <td className="py-2.5 px-3 font-medium"><FileText size={12} className="inline mr-1.5" />{d.title}</td>
                  <td className="py-2.5 px-3"><span className="badge b-muted">{KIND_LABEL[d.kind] || d.kind}</span></td>
                  <td className="py-2.5 px-3 font-mono">{d.chunks}</td>
                  <td className="py-2.5 px-3 font-mono" style={{ color: "var(--text-muted)" }}>{d.chars} chars</td>
                  <td className="py-2.5 px-3 text-right whitespace-nowrap">
                    {data.enabled && (
                      <>
                        <button className="btn btn-ghost" style={{ padding: "3px 8px" }}
                                onClick={() => edit(d.id)} disabled={busy} title="View / edit"
                                data-testid={`edit-${d.slug}`}>
                          <Pencil size={12} />
                        </button>
                        <button className="btn btn-ghost" style={{ padding: "3px 8px" }}
                                onClick={() => toggleHistory(d.id)} disabled={busy} title="Edit history"
                                data-testid={`history-${d.slug}`}>
                          <History size={12} />
                        </button>
                        <button className="btn btn-ghost" style={{ padding: "3px 8px" }}
                                onClick={() => remove(d.id)} disabled={busy} title="Delete">
                          <Trash2 size={12} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
                {historyDocId === d.id && (
                  <tr style={{ borderBottom: "1px solid var(--border)" }}>
                    <td colSpan={5} className="p-0">
                      <div className="p-3" style={{ background: "var(--surface-muted, rgba(127,127,127,0.06))" }}
                           data-testid="revision-history">
                        <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>
                          Edit history — {d.title}
                        </div>
                        {revisions && revisions.length === 0 && (
                          <div className="text-[12.5px]" style={{ color: "var(--text-muted)" }}>
                            No history yet.
                          </div>
                        )}
                        {revisions && revisions.length > 0 && (
                          <table className="w-full text-[12.5px]">
                            <thead>
                              <tr className="text-left" style={{ color: "var(--text-muted)" }}>
                                <th className="py-1 pr-3 font-medium text-[10.5px] uppercase tracking-wider">Rev</th>
                                <th className="py-1 pr-3 font-medium text-[10.5px] uppercase tracking-wider">When</th>
                                <th className="py-1 pr-3 font-medium text-[10.5px] uppercase tracking-wider">Who</th>
                                <th className="py-1 pr-3 font-medium text-[10.5px] uppercase tracking-wider">Size</th>
                                <th className="py-1"></th>
                              </tr>
                            </thead>
                            <tbody>
                              {revisions.map((r) => (
                                <tr key={r.revision} data-testid={`revision-${d.slug}-${r.revision}`}>
                                  <td className="py-1 pr-3 font-mono">
                                    {r.revision}{r.is_current ? " (current)" : ""}
                                  </td>
                                  <td className="py-1 pr-3 font-mono text-[11.5px]" title={r.created_at}>
                                    {fmtTimeAgo(r.created_at)}
                                  </td>
                                  <td className="py-1 pr-3">{r.created_by || "—"}</td>
                                  <td className="py-1 pr-3 font-mono" style={{ color: "var(--text-muted)" }}>
                                    {r.chars} chars
                                  </td>
                                  <td className="py-1">
                                    {!r.is_current && (
                                      <button className="btn btn-ghost" style={{ padding: "2px 8px" }}
                                              onClick={() => viewRevision(d.id, r.revision)} disabled={busy}>
                                        View
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
